import { type ReactNode, useEffect } from "react";
import { useChat } from "../../providers/chat-provider";

export type FooterButtonsProps = {
  children: ReactNode;
  /**
   * Stable identity of the rendered buttons. When the same `FooterButtons`
   * instance stays mounted but its `children` change (e.g. the last bot message
   * is replaced in `onlyLastMessage` mode), this key lets the effect re-sync the
   * footer content. It must NOT be derived from `children` identity (a new React
   * element every render) or the effect would loop; use the underlying data.
   */
  contentKey?: string;
};

export const FooterButtons = ({ children, contentKey }: FooterButtonsProps) => {
  const { setButtonsContent } = useChat();

  useEffect(() => {
    const registered = children;

    setButtonsContent(registered);

    // Withdraw only what is still ours. A message leaving the conversation stays mounted
    // for the length of its exit animation, so the message replacing it registers its own
    // buttons FIRST and this cleanup runs AFTER — verified ordering, not a guess. Clearing
    // unconditionally would wipe the newcomer's buttons and leave the footer empty for the
    // rest of the conversation.
    return () => setButtonsContent((current) => (current === registered ? null : current));
    // `children` is intentionally excluded: it is a new element on every render.
    // `contentKey` captures the meaningful identity of the buttons instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setButtonsContent, contentKey]);

  return null;
};
